import React, { useState } from 'react';
import CustomInput from '../../common/CustomInput';

const AddAchievementModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    title: '',
    club: '',
    year: '',
    image: null,
    certificate: null
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const achievementData = {
      title: formData.title,
      club: formData.club,
      year: formData.year,
      image: formData.image,
      certificate: formData.certificate
    };
    
    onAdd(achievementData);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleCertificateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        certificate: file
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        {/* Header */}
        {/*
          The heading is the longest of all the profile dialogs, and on a
          narrow screen it used to shove Cancel/Save right off the edge — 7px
          past at 375px, 62px at 320px, i.e. the dialog could not be saved on a
          phone at all. `flex-wrap` drops the buttons onto their own line only
          when they no longer fit, and `flex-shrink-0` stops them being
          squeezed; from 768px up both still sit on one line as before.
        */}
        <div className="flex flex-wrap gap-3 justify-between items-center border-b border-[#E6E6E6] pb-6 mb-6">
          <h2 className="text-[16px] font-bold text-[#3C3C3C]">ADD ACHIEVEMENTS/TROPHIES</h2>
          <div className="flex gap-2 flex-shrink-0 ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-[#E6E6E6] bg-white text-[#1E1E1E] rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-[#2283FF] text-white rounded-lg"
            >
              Save
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload Section */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              {formData.image ? (
                <img
                  src={URL.createObjectURL(formData.image)}
                  alt="Achievement"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to trophy icon if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-full h-full bg-yellow-500 flex items-center justify-center ${formData.image ? 'hidden' : ''}`}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 9H4.5C4.5 6.51472 6.51472 4.5 9 4.5H15C17.4853 4.5 19.5 6.51472 19.5 9H18C18 7.34315 16.6569 6 15 6H9C7.34315 6 6 7.34315 6 9Z" fill="white"/>
                  <path d="M12 4.5V6M12 18V19.5M4.5 12H6M18 12H19.5M7.5 7.5L8.5 8.5M15.5 15.5L16.5 16.5M8.5 15.5L7.5 16.5M16.5 7.5L15.5 8.5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M12 6C14.2091 6 16 7.79086 16 10V12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12V10C8 7.79086 9.79086 6 12 6Z" fill="white"/>
                </svg>
              </div>
            </div>
            <button
              type="button"
              onClick={() => document.getElementById('achievement-image').click()}
              className="px-4 py-2 border border-[#E6E6E6] bg-white text-[#1E1E1E] rounded-lg flex items-center gap-2"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 9V11.6667C13 12.0203 12.8595 12.3594 12.6095 12.6095C12.3594 12.8595 12.0203 13 11.6667 13H2.33333C1.97971 13 1.64057 12.8595 1.39052 12.6095C1.14048 12.3594 1 12.0203 1 11.6667V9M10.3333 4.33333L7 1M7 1L3.66667 4.33333M7 1V9" stroke="#2C2C2C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Upload image
            </button>
            <input
              id="achievement-image"
              type="file"
              onChange={handleImageUpload}
              className="hidden"
              accept=".jpg,.jpeg,.png,.gif"
            />
            {formData.image && (
              <p className="text-sm text-gray-600">
                Selected: {formData.image.name}
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-bold text-[#3C3C3C] mb-2">
              ADD TITLE
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="Add title here"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[#1A212FB2] text-[14px] placeholder:text-[14px] font-medium placeholder:font-medium placeholder:text-[#1A212F66]"
            />
          </div>

          {/* Club and Year */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#3C3C3C] mb-2">
                CLUB (OPTIONAL)
              </label>
              <input
                type="text"
                value={formData.club}
                onChange={(e) => handleInputChange("club", e.target.value)}
                // Was "Add License ID here", copied verbatim from the LICENSE ID
                // field in AddLicenseModal.js. "Enter club name" is the string
                // the club-name input in AccountInformation.js already uses, so
                // it is registered in all 16 languages already.
                placeholder="Enter club name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[#1A212FB2] text-[14px] placeholder:text-[14px] font-medium placeholder:font-medium placeholder:text-[#1A212F66]"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#3C3C3C] mb-2">
                YEAR
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => handleInputChange("year", e.target.value)}
                  placeholder="YYYY"
                  min="1900"
                  max="2030"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[#1A212FB2] text-[14px] placeholder:text-[14px] font-medium placeholder:font-medium placeholder:text-[#1A212F66]"
                />
                <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Upload Certificate */}
          <div>
            <button
              type="button"
              onClick={() => document.getElementById('achievement-certificate').click()}
              className="w-fit px-4 py-2 border border-[#E6E6E6] bg-white text-[#1E1E1E] rounded-lg flex items-center gap-2"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 9V11.6667C13 12.0203 12.8595 12.3594 12.6095 12.6095C12.3594 12.8595 12.0203 13 11.6667 13H2.33333C1.97971 13 1.64057 12.8595 1.39052 12.6095C1.14048 12.3594 1 12.0203 1 11.6667V9M10.3333 4.33333L7 1M7 1L3.66667 4.33333M7 1V9" stroke="#2C2C2C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Upload certificate
            </button>
            <input
              id="achievement-certificate"
              type="file"
              onChange={handleCertificateUpload}
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            {formData.certificate && (
              <p className="text-sm text-gray-600 mt-2">
                Selected: {formData.certificate.name}
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAchievementModal; 